import type { OrganizationDraftYearsResponse } from '@on3/api';
import type { IDraftHistoryPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { useSite } from '@on3/ui-lib/index';
import { TeamDraftSummary } from '@public/components/DraftByStars/Summary/TeamDraftSummary';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { Filter, makeList } from '../Filters/Filter';
import { FilterTeams } from '../Filters/FilterTeams';
import { DraftByCategoryNavigation } from './DraftByCategoryNavigation';
import { DraftHistoryItem } from './DraftHistoryItem';
import styles from './DraftHistoryList.module.scss';
import { DraftHistoryListHeader } from './DraftHistoryListHeader';
import { PicksByStars } from './DraftModules/PicksByStars';
import { PicksByState } from './DraftModules/PicksByState';
import { PicksByYear } from './DraftModules/PicksByYear';

export const DraftHistoryList = ({
  draftList,
  filters,
  modules,
  title,
  currentTeam,
}: IDraftHistoryPageProps) => {
  const { defaultYears } = useSite();
  const { nflDraftYear, nbaDraftYear } = defaultYears;
  const { yearPicks, statePicks, starPicks, starSummary } = modules;
  const { years } = filters;
  const [list, setList] = useState(
    draftList.list?.filter((d) => d.year && d.year > 2011),
  );
  const [selected, setSelected] =
    useState<keyof OrganizationDraftYearsResponse>('year');
  const router = useRouter();
  const isNBA = router.asPath.includes('nba');
  const league = isNBA ? 'nba' : 'nfl';
  const { year, 'team-key': teamKey, 'year-span': span } = router.query;
  const leagueDraftYear = isNBA ? nbaDraftYear : nflDraftYear;
  const availableYears = years?.filter(
    (yr: number) => yr <= leagueDraftYear && yr > 2011,
  );
  const showStarSummary = !!starSummary?.totalDrafted;
  const showStarPicksSummary = !!starPicks?.total;
  const basePath = router.asPath.split('?')[0];
  const beginingYear = 2000;

  const sortList = useCallback(
    (sortBy: keyof OrganizationDraftYearsResponse, ascending: boolean) => {
      const sortedList = list?.sort((a, b) => {
        const setA = a[sortBy] || 0;
        const setB = b[sortBy] || 0;

        if (setA < setB) {
          return ascending ? 1 : -1;
        }

        if (setA > setB) {
          return ascending ? -1 : 1;
        }

        return 0;
      });

      sortedList && setList([...sortedList]);
    },
    [list],
  );
  const getHref = (spanNum: number) => {
    if (spanNum === 1) {
      return `${basePath}?team-key=${teamKey}`;
    }

    return {
      pathname: router.pathname,
      query: { ...router.query, 'year-span': spanNum },
    };
  };

  return (
    <section>
      <header className={styles.pageHeader} key={`${teamKey}`}>
        {currentTeam?.siteSlug && (
          // TODO: we need the organization asset url
          <TeamAvatar
            className={styles.teamLogo}
            height={50}
            slug={currentTeam?.siteSlug}
            src={`https://on3static.com/teams/${currentTeam.siteSlug}.svg`}
            width={50}
          />
        )}

        <Text className={styles.pageTitle} variant="h1">
          {title}
        </Text>
      </header>

      <div className={styles.filterWrapper}>
        <div className={styles.spanLinkContainer}>
          {span && +span !== 1 && (
            <TextLink
              className={styles.spanLink}
              href={getHref(1)}
              underline="none"
              variant="button"
            >
              1 yr
            </TextLink>
          )}
          <TextLink
            className={clsx(styles.spanLink, {
              [styles.active]: span === '3',
            })}
            href={getHref(3)}
            underline="none"
            variant="button"
          >
            Last 3 Yrs
          </TextLink>

          <TextLink
            className={clsx(styles.spanLink, {
              [styles.active]: span === '5',
            })}
            href={getHref(5)}
            underline="none"
            variant="button"
          >
            Last 5 Yrs
          </TextLink>
        </div>
        <div className={styles.filtersContainer}>
          <div className={styles.dropdownContainer}>
            <Filter
              list={makeList(availableYears)}
              name="year"
              selected={`${year}`}
            />
            <FilterTeams
              name="team-key"
              selected={`${teamKey}`}
              teams={filters.teams}
              useOrgKey
            />
          </div>
          <TextLink
            className={styles.draftLink}
            color="primary"
            href={`/${league}/draft/${year}/draft-history/team-draft-totals/`}
            underline="none"
            variant="subtitle1"
          >
            College Team Draft Totals
          </TextLink>
        </div>
      </div>
      <DraftByCategoryNavigation />
      {showStarSummary && <TeamDraftSummary starSummary={starSummary} />}
      <section>
        {!!list?.length && (
          <>
            <DraftHistoryListHeader
              selected={selected}
              setSelected={setSelected}
              sortList={sortList}
            />

            <ul>
              {list?.map((item, index) => {
                return (
                  !!list?.length && (
                    <DraftHistoryItem {...item} index={index} key={index} />
                  )
                );
              })}
            </ul>
          </>
        )}
        {!list?.length && (
          <Text>There is no draft data available for this selection.</Text>
        )}
      </section>
      <div className={styles.modulesContainer}>
        <div className={styles.yearBlock}>
          {!!yearPicks.list?.length && (
            <PicksByYear
              picksTitle={`Picks By Year (${beginingYear} - ${year})`}
              yearPicks={yearPicks}
            />
          )}
        </div>
        <div className={styles.stateBlock}>
          {!!statePicks.list?.length && (
            <PicksByState
              picksTitle={`Draft Picks By State (${beginingYear} - ${year})`}
              statePicks={statePicks}
            />
          )}
        </div>
        <div className={styles.starsBlock}>
          {showStarPicksSummary && (
            <PicksByStars
              picksTitle={`Draft Picks Stars Distribution (${beginingYear} - ${year})`}
              starPicks={starPicks}
            />
          )}
        </div>
      </div>
    </section>
  );
};
